// TimerDisplay — monospace HH:MM:SS read-out. Damped-red variant when
// `expired` (LiveCook past nominal duration). Presentational only — the
// consumer owns the tick.
//
// With an optional `totalSeconds` it reads "elapsed / total"
// (e.g. 00:05:45 / 01:30:00) — used by the LiveCook timer to show the
// expected cooking time alongside the time passed.
//
// See README §6.7.3 item 6 (LiveCook 00:00:00 counting up, turns red
// past nominal duration) and §7.1 (`accent.late`).

import { theme } from '../theme';

export interface TimerDisplayProps {
  seconds: number;
  /** Optional expected total; when > 0 the read-out shows "elapsed / total". */
  totalSeconds?: number;
  expired?: boolean;
  label?: string;
}

function format(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(safe / 3600);
  const m = Math.floor((safe % 3600) / 60);
  const s = safe % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

export function TimerDisplay({ seconds, totalSeconds, expired = false, label }: TimerDisplayProps) {
  const elapsed = format(seconds);
  const hasTotal = typeof totalSeconds === 'number' && totalSeconds > 0;
  const total = hasTotal ? format(totalSeconds) : '';
  const text = hasTotal ? `${elapsed} / ${total}` : elapsed;
  const ariaTime = hasTotal ? `${elapsed} of ${total}` : elapsed;
  const colour = expired ? theme.accent.late : theme.text.primary;
  return (
    <span
      role="timer"
      aria-label={label ? `${label}: ${ariaTime}` : ariaTime}
      style={{
        fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
        fontVariantNumeric: 'tabular-nums',
        fontSize: '1.25rem',
        fontWeight: 600,
        color: colour,
        letterSpacing: '0.04em',
      }}
    >
      {text}
    </span>
  );
}

export default TimerDisplay;
